import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner, faArrowUp, faArrowDown, faTriangleExclamation, faWeightHanging,
} from '@fortawesome/free-solid-svg-icons';
import analyticsIntelligenceService from '../services/analyticsIntelligenceService';
import productCatalogueService from '../services/productCatalogueService';
import SearchableSelect from '../components/common/SearchableSelect';
import { useMessageAlert } from '../components/common/MessageAlert';
import { useWarehouseContext } from '../context/WarehouseContext';
import { UI_COLORS, colorHelpers } from '../utils/colors';

const TABS = [
  { id: 'forecast', label: 'Forecast' },
  { id: 'anomalies', label: 'Anomalies' },
  { id: 'reorder', label: 'Reorder Suggestions' },
];

const formatKg = (kg) => {
  const n = Number(kg);
  if (!Number.isFinite(n)) return '—';
  return `${n.toLocaleString('en-IN', { maximumFractionDigits: 1 })} kg`;
};

const Badge = ({ text, tone = 'info' }) => {
  const color = {
    high: UI_COLORS.STATUS.SUCCESS,
    low: UI_COLORS.STATUS.WARNING,
    info: UI_COLORS.STATUS.INFO,
    error: UI_COLORS.STATUS.ERROR,
    muted: UI_COLORS.TEXT.MUTED,
  }[tone] || UI_COLORS.STATUS.INFO;
  return (
    <span
      className="text-[11px] px-2 py-0.5 rounded-full font-semibold whitespace-nowrap"
      style={{ color, backgroundColor: colorHelpers.withOpacity(color, 0.12) }}
    >
      {text}
    </span>
  );
};

const Panel = ({ title, subtitle, children }) => (
  <div className="bg-white rounded-xl border p-5 mb-5" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
    <h2 className="text-lg font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{title}</h2>
    {subtitle && <p className="text-sm mt-1 mb-3" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{subtitle}</p>}
    {children}
  </div>
);

const Empty = ({ children }) => (
  <p className="text-sm py-3" style={{ color: UI_COLORS.TEXT.MUTED }}>{children}</p>
);

/**
 * Sub Category (product catalogue) options, loaded once per page.
 *
 * This is the only filter here that narrows the SERVER's work rather than the
 * rendered output: `catalogue_ids` restricts which products the weekly aggregate
 * scans, so a narrow selection genuinely shortens the query. Search and pagination
 * only trim what has already been computed.
 *
 * Note the anomalies endpoint does NOT accept catalogue_ids, so this filter is
 * deliberately absent from that tab rather than shown and silently ignored.
 */
const useCatalogueOptions = () => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await productCatalogueService.getAllProducts();
        if (cancelled) return;
        const list = Array.isArray(res?.data) ? res.data : [];
        setOptions(list.map((c) => ({
          value: c.id,
          label: c.product_name || c.name || c.alias_name || `Sub category ${c.id}`,
        })));
      } catch (e) {
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return { options, loading };
};

const CatalogueFilter = ({ value, onChange, options, loading }) => (
  <label className="text-sm" style={{ minWidth: 220 }}>
    <span className="block text-xs font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>Sub Category</span>
    <SearchableSelect
      isMulti
      options={options}
      value={value}
      onChange={onChange}
      loading={loading}
      placeholder="All sub categories"
    />
  </label>
);

const PAGE_SIZE = 25;

// Searchable columns per tab, hoisted so the hook's deps stay stable.
const FORECAST_FIELDS = ['product', 'category'];
const REORDER_FIELDS = ['product', 'lead_time_source'];

/**
 * Search + paginate an already-fetched list.
 *
 * Deliberately client-side. These endpoints have no skip/limit/search params, and
 * adding them would not speed anything up: the server recomputes the whole
 * multi-week per-product aggregate before it can return a single row, so slicing the
 * output saves payload and DOM nodes, not wait time. What this does fix is the part
 * that IS local — finding one product in a long list, and rendering hundreds of table
 * rows at once.
 */
const useSearchPage = (rows, fields) => {
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      fields.some((f) => String(r?.[f] ?? '').toLowerCase().includes(q)));
  }, [rows, query, fields]);

  // A narrowing search must not strand the user on a page that no longer exists.
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageRows = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  return {
    query,
    setQuery: (v) => { setQuery(v); setPage(1); },
    page: safePage, setPage, totalPages,
    pageRows, total: filtered.length, unfilteredTotal: rows.length,
  };
};

const SearchBox = ({ value, onChange, placeholder }) => (
  <input
    type="search" value={value} onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder || 'Search…'}
    className="rounded-lg border px-3 py-2 text-sm w-full sm:w-64"
    style={{ borderColor: UI_COLORS.FORMS.INPUT.border, backgroundColor: UI_COLORS.FORMS.INPUT.background }}
  />
);

const Pager = ({ page, totalPages, setPage, total, unfilteredTotal }) => (
  <div className="flex items-center justify-between gap-3 mt-3 flex-wrap">
    <span className="text-xs" style={{ color: UI_COLORS.TEXT.MUTED }}>
      {total === unfilteredTotal
        ? `${total} rows`
        : `${total} of ${unfilteredTotal} rows`}
    </span>
    {totalPages > 1 && (
      <div className="flex items-center gap-2">
        <button
          type="button" disabled={page <= 1} onClick={() => setPage(page - 1)}
          className="px-3 py-1.5 rounded-lg text-sm border disabled:opacity-40"
          style={{ borderColor: UI_COLORS.BORDERS.MEDIUM }}
        >Previous</button>
        <span className="text-xs" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
          Page {page} of {totalPages}
        </span>
        <button
          type="button" disabled={page >= totalPages} onClick={() => setPage(page + 1)}
          className="px-3 py-1.5 rounded-lg text-sm border disabled:opacity-40"
          style={{ borderColor: UI_COLORS.BORDERS.MEDIUM }}
        >Next</button>
      </div>
    )}
  </div>
);

// ---------------------------------------------------------------------------

const ForecastTab = ({ warehouseIds, catalogue }) => {
  const { showError } = useMessageAlert();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // 8 matches the API's own DEFAULT_WEEKS. The previous 12 made every load scan
  // 50% more history than the server would have chosen for itself.
  const [windowWeeks, setWindowWeeks] = useState(8);
  const [horizon, setHorizon] = useState(4);

  const load = useCallback(async (force = false) => {
    setLoading(true);
    try {
      const res = await analyticsIntelligenceService.getForecast({
        window_weeks: windowWeeks,
        horizon,
        ...(warehouseIds?.length ? { warehouse_ids: warehouseIds } : {}),
        ...(catalogue.value?.length ? { catalogue_ids: catalogue.value } : {}),
      }, { force });
      if (res.success) setData(res.data); else showError(res.error);
    } catch (err) {
      showError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  // showError is intentionally NOT a dependency: useMessageAlert returns a fresh
  // function identity on every render, so including it rebuilds `load`, which
  // re-fires the effect, which re-renders — an endless fetch loop.
  }, [windowWeeks, horizon, warehouseIds, catalogue.value]);

  useEffect(() => { load(); }, [load]);

  const products = useMemo(() => data?.products || [], [data]);
  const pager = useSearchPage(products, FORECAST_FIELDS);

  return (
    <Panel
      title="Demand forecast"
      subtitle={
        data && data.total_products > data.returned_products
          ? `Projected weekly sales in kg. ${data.total_products - data.returned_products} products with no movement in this window are hidden.`
          : "Projected weekly sales in kg. Confidence is 'low' where history is thin — treat those as directional."
      }
    >
      <div className="flex flex-wrap gap-3 mb-4">
        <label className="text-sm">
          <span className="block text-xs font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>History (weeks)</span>
          <input
            type="number" min={4} max={52} value={windowWeeks}
            onChange={(e) => setWindowWeeks(Math.min(52, Math.max(4, Number(e.target.value) || 4)))}
            className="rounded-lg border px-3 py-2 text-sm w-28"
            style={{ borderColor: UI_COLORS.FORMS.INPUT.border }}
          />
        </label>
        <label className="text-sm">
          <span className="block text-xs font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>Horizon (weeks)</span>
          <input
            type="number" min={1} max={4} value={horizon}
            onChange={(e) => setHorizon(Math.min(4, Math.max(1, Number(e.target.value) || 1)))}
            className="rounded-lg border px-3 py-2 text-sm w-28"
            style={{ borderColor: UI_COLORS.FORMS.INPUT.border }}
          />
        </label>
        <CatalogueFilter {...catalogue} />
        <label className="text-sm flex-1 min-w-[200px]">
          <span className="block text-xs font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>Search</span>
          <SearchBox value={pager.query} onChange={pager.setQuery} placeholder="Product or category…" />
        </label>
        <button
          type="button" onClick={() => load(true)} disabled={loading}
          className="px-3 py-2 rounded-lg text-sm border disabled:opacity-40"
          style={{ borderColor: UI_COLORS.BORDERS.MEDIUM }}
          title="Recompute on the server, bypassing the 5-minute cache"
        >Refresh</button>
      </div>

      {loading ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
        : products.length === 0 ? <Empty>No product movement in this window.</Empty>
        : pager.total === 0 ? <Empty>No product matches that search.</Empty> : (
          <>
            <div className="overflow-x-auto rounded-lg border" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
            <table className="w-full text-sm">
              <thead>
                <tr style={{ backgroundColor: UI_COLORS.BACKGROUNDS.SECTION }}>
                  <th className="text-left px-3 py-2 font-semibold">Product</th>
                  <th className="text-left px-3 py-2 font-semibold">Category</th>
                  {Array.from({ length: horizon }).map((_, i) => (
                    <th key={i} className="text-right px-3 py-2 font-semibold">Week +{i + 1}</th>
                  ))}
                  <th className="text-left px-3 py-2 font-semibold">Method</th>
                  <th className="text-right px-3 py-2 font-semibold">Lead days</th>
                </tr>
              </thead>
              <tbody>
                {pager.pageRows.map((p, i) => (
                  <tr key={i} className="border-t" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                    <td className="px-3 py-2 font-medium">{p.product || '—'}</td>
                    <td className="px-3 py-2">{p.category || '—'}</td>
                    {Array.from({ length: horizon }).map((_, w) => (
                      <td key={w} className="px-3 py-2 text-right">
                        {formatKg(p.forecast_sales_kg_next_weeks?.[w])}
                      </td>
                    ))}
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1 flex-wrap">
                        <Badge text={(p.forecast_method || '').replace(/_/g, ' ') || '—'} tone="muted" />
                        <Badge text={p.forecast_confidence || '—'} tone={p.forecast_confidence === 'high' ? 'high' : 'low'} />
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right">{p.avg_supplier_lead_days ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            <Pager {...pager} />
          </>
        )}
    </Panel>
  );
};

// ---------------------------------------------------------------------------

const ANOMALY_SECTIONS = [
  ['product_movement', 'Product movement'],
  ['business', 'Business'],
  ['duplicate_receipts', 'Duplicate receipts'],
  ['negative_stock', 'Negative stock'],
  ['clamp_divergence', 'Clamp divergence'],
];

const AnomaliesTab = ({ warehouseIds }) => {
  const { showError } = useMessageAlert();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');

  // One box filters every family at once. The sections are individually short, so
  // paging them would add controls without removing rows worth removing.
  const matches = useCallback((a) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return [a?.product, a?.category, a?.type, a?.message]
      .some((v) => String(v ?? '').toLowerCase().includes(q));
  }, [query]);

  const load = useCallback(async (force = false) => {
    setLoading(true);
    try {
      const res = await analyticsIntelligenceService.getAnomalies(
        warehouseIds?.length ? { warehouse_ids: warehouseIds } : {},
        { force },
      );
      if (res.success) setData(res.data); else showError(res.error);
    } catch (err) {
      showError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  // showError is intentionally NOT a dependency: useMessageAlert returns a fresh
  // function identity on every render, so including it rebuilds `load`, which
  // re-fires the effect, which re-renders — an endless fetch loop.
  }, [warehouseIds]);

  useEffect(() => { load(); }, [load]);

  const renderRow = (a, i) => {
    const isSpike = a.direction === 'spike';
    // negative_stock / duplicate_receipts / clamp_divergence carry everything they
    // have to say in `detail` — without it every row in those families rendered the
    // same humanised `type` ("Negative Stock") fifty times over, saying nothing.
    const detail = a.detail || a.message || null;
    // Only the product families carry a `product`. Business rows identify themselves
    // solely through `type` ("business_dispatched_kg"), so fall back to that with the
    // family prefix stripped.
    const label = a.product
      || (a.type ? a.type.replace(/^(business|product)_/, '').replace(/_/g, ' ') : null)
      || 'Anomaly';
    // `type` is product_<scope> (sales vs purchase), so one product legitimately
    // appears once per scope. Showing the scope stops those reading as duplicates.
    const scope = a.product && a.type ? a.type.replace(/^product_/, '') : null;
    return (
      <div key={i} className="border-t px-3 py-2 text-sm" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <span className="font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
            {a.direction && (
              <FontAwesomeIcon
                icon={isSpike ? faArrowUp : faArrowDown}
                className="mr-2 text-xs"
                style={{ color: isSpike ? UI_COLORS.STATUS.SUCCESS : UI_COLORS.STATUS.ERROR }}
              />
            )}
            <span className="capitalize">{label}</span>
            {a.category && (
              <span className="ml-2 text-xs font-normal" style={{ color: UI_COLORS.TEXT.MUTED }}>{a.category}</span>
            )}
            {scope && (
              <span className="ml-2 text-xs font-normal capitalize" style={{ color: UI_COLORS.TEXT.MUTED }}>· {scope}</span>
            )}
          </span>
          <div className="flex items-center gap-1 flex-wrap">
            {a.branch && <Badge text={String(a.branch).replace(/_/g, ' ')} tone="muted" />}
            {a.confidence && <Badge text={a.confidence} tone={a.confidence === 'high' ? 'high' : 'low'} />}
          </div>
        </div>
        {detail && (
          <div className="text-xs mt-1" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{detail}</div>
        )}
        {(a.value !== undefined || a.baseline_median !== undefined) && (
          <div className="text-xs mt-1" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
            value {formatKg(a.value)} vs median {formatKg(a.baseline_median)}
            {a.robust_z !== undefined && a.robust_z !== null && ` · z ${Number(a.robust_z).toFixed(2)}`}
            {a.pct_vs_median !== undefined && a.pct_vs_median !== null && ` · ${Number(a.pct_vs_median).toFixed(1)}%`}
            {a.wow_pct !== undefined && a.wow_pct !== null && ` · WoW ${Number(a.wow_pct).toFixed(0)}%`}
          </div>
        )}
      </div>
    );
  };

  return (
    <Panel
      title="Anomalies"
      subtitle="Each family is detected independently — an empty section means that detector found nothing, not that it failed."
    >
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <SearchBox value={query} onChange={setQuery} placeholder="Product, category or metric…" />
        <button
          type="button" onClick={() => load(true)} disabled={loading}
          className="px-3 py-2 rounded-lg text-sm border disabled:opacity-40"
          style={{ borderColor: UI_COLORS.BORDERS.MEDIUM }}
          title="Recompute on the server, bypassing the 5-minute cache"
        >Refresh</button>
      </div>

      {loading ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : (
        ANOMALY_SECTIONS.map(([key, label]) => {
          const rows = (data?.[key] || []).filter(matches);
          // counts[] is the family size BEFORE the server's per-family cap.
          const fullCount = data?.counts?.[key] ?? (data?.[key] || []).length;
          return (
            <div key={key} className="mb-5">
              <h3 className="text-sm font-bold mb-1 flex items-center gap-2" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                {label}
                <Badge
                  text={fullCount > rows.length ? `${rows.length} of ${fullCount}` : String(rows.length)}
                  tone={rows.length ? 'info' : 'muted'}
                />
              </h3>
              {rows.length === 0
                ? <Empty>{query.trim() ? 'No match in this section.' : 'Nothing flagged.'}</Empty>
                : <div className="rounded-lg border" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>{rows.map(renderRow)}</div>}
            </div>
          );
        })
      )}
    </Panel>
  );
};

// ---------------------------------------------------------------------------

const ReorderTab = ({ warehouseIds, catalogue }) => {
  const { showError } = useMessageAlert();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [coverageDays, setCoverageDays] = useState(30);

  const load = useCallback(async (force = false) => {
    setLoading(true);
    try {
      const res = await analyticsIntelligenceService.getReorderSuggestions({
        coverage_days: coverageDays,
        ...(warehouseIds?.length ? { warehouse_ids: warehouseIds } : {}),
        ...(catalogue.value?.length ? { catalogue_ids: catalogue.value } : {}),
      }, { force });
      if (res.success) setData(res.data); else showError(res.error);
    } catch (err) {
      showError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  // showError is intentionally NOT a dependency: useMessageAlert returns a fresh
  // function identity on every render, so including it rebuilds `load`, which
  // re-fires the effect, which re-renders — an endless fetch loop.
  }, [coverageDays, warehouseIds, catalogue.value]);

  useEffect(() => { load(); }, [load]);

  const suggestions = useMemo(() => data?.suggestions || [], [data]);
  const needsReview = useMemo(() => data?.needs_review || [], [data]);
  const pager = useSearchPage(suggestions, REORDER_FIELDS);

  return (
    <>
      <Panel
        title="Reorder suggestions"
        subtitle={
          data && data.total_suggestions > suggestions.length
            ? `Advisory only — nothing here creates a purchase order. Showing the ${suggestions.length} biggest of ${data.total_suggestions} qualifying products.`
            : 'Advisory only — nothing here creates a purchase order. Accuracy depends on stock being maintained; where it is not, treat these as directional.'
        }
      >
        <div className="flex flex-wrap items-end gap-3 mb-4">
        <label className="text-sm">
          <span className="block text-xs font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>Coverage (days)</span>
          <input
            type="number" min={1} max={180} value={coverageDays}
            onChange={(e) => setCoverageDays(Math.min(180, Math.max(1, Number(e.target.value) || 1)))}
            className="rounded-lg border px-3 py-2 text-sm w-28"
            style={{ borderColor: UI_COLORS.FORMS.INPUT.border }}
          />
        </label>
        <CatalogueFilter {...catalogue} />
        <label className="text-sm flex-1 min-w-[200px]">
          <span className="block text-xs font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>Search</span>
          <SearchBox value={pager.query} onChange={pager.setQuery} placeholder="Product…" />
        </label>
        <button
          type="button" onClick={() => load(true)} disabled={loading}
          className="px-3 py-2 rounded-lg text-sm border disabled:opacity-40"
          style={{ borderColor: UI_COLORS.BORDERS.MEDIUM }}
          title="Recompute on the server, bypassing the 5-minute cache"
        >Refresh</button>
        </div>

        {loading ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
          : suggestions.length === 0 ? <Empty>No reorder suggestions for this selection.</Empty>
          : pager.total === 0 ? <Empty>No product matches that search.</Empty> : (
            <>
            <div className="overflow-x-auto rounded-lg border" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ backgroundColor: UI_COLORS.BACKGROUNDS.SECTION }}>
                    <th className="text-left px-3 py-2 font-semibold">Product</th>
                    <th className="text-right px-3 py-2 font-semibold">Suggested pcs</th>
                    <th className="text-right px-3 py-2 font-semibold">Suggested kg</th>
                    <th className="text-left px-3 py-2 font-semibold">Lead time from</th>
                    <th className="text-left px-3 py-2 font-semibold">Flags</th>
                  </tr>
                </thead>
                <tbody>
                  {pager.pageRows.map((s, i) => (
                    <tr key={i} className="border-t" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                      <td className="px-3 py-2 font-medium">{s.product || '—'}</td>
                      <td className="px-3 py-2 text-right">{s.suggested_pieces ?? '—'}</td>
                      <td className="px-3 py-2 text-right">{formatKg(s.suggested_kg)}</td>
                      <td className="px-3 py-2">
                        <Badge text={s.lead_time_source || '—'} tone="muted" />
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex gap-1 flex-wrap">
                          {(s.flags || []).map((f, j) => (
                            <Badge key={j} text={f.replace(/_/g, ' ')} tone={f === 'oversold_negative_stock' ? 'error' : 'low'} />
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pager {...pager} />
            </>
          )}
      </Panel>

      <Panel
        title={`Needs review (${needsReview.length})`}
        subtitle="These products have no weight on file, so a quantity cannot be computed. They are not zero-quantity suggestions — set the product weight and they will join the list above."
      >
        {loading ? null : needsReview.length === 0 ? <Empty>Nothing needs review.</Empty> : (
          <div className="rounded-lg border" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
            {needsReview.map((s, i) => (
              <div key={i} className="flex items-center justify-between gap-3 border-t px-3 py-2 text-sm flex-wrap"
                style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                <span className="font-medium">
                  <FontAwesomeIcon icon={faTriangleExclamation} className="mr-2 text-xs" style={{ color: UI_COLORS.STATUS.WARNING }} />
                  {s.product || '—'}
                </span>
                <span className="inline-flex items-center gap-2">
                  {(s.flags || []).map((f, j) => <Badge key={j} text={f.replace(/_/g, ' ')} tone="low" />)}
                  <span className="inline-flex items-center gap-1 text-xs font-semibold" style={{ color: UI_COLORS.STATUS.WARNING }}>
                    <FontAwesomeIcon icon={faWeightHanging} className="text-xs" />
                    Set product weight
                  </span>
                </span>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </>
  );
};

// ---------------------------------------------------------------------------

const IntelligencePage = () => {
  const [tab, setTab] = useState('forecast');
  const { selectedWarehouseIds } = useWarehouseContext();
  const { options, loading: catalogueLoading } = useCatalogueOptions();
  const [catalogueIds, setCatalogueIds] = useState([]);
  const catalogue = useMemo(
    () => ({ value: catalogueIds, onChange: setCatalogueIds, options, loading: catalogueLoading }),
    [catalogueIds, options, catalogueLoading],
  );

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-6 w-full max-w-full">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Analytics Intelligence</h1>
        <p className="mt-2 text-gray-600">
          Forecasting, anomaly detection and reorder advice. All figures are weight and counts — no financial series.
        </p>
      </div>

      <div className="flex gap-1 mb-5 border-b" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
        {TABS.map((tb) => (
          <button
            key={tb.id} type="button" onClick={() => setTab(tb.id)}
            className="px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px"
            style={{
              color: tab === tb.id ? UI_COLORS.BRAND.TERTIARY : UI_COLORS.TEXT.SECONDARY,
              borderColor: tab === tb.id ? UI_COLORS.BRAND.TERTIARY : 'transparent',
            }}
          >
            {tb.label}
          </button>
        ))}
      </div>

      {tab === 'forecast' && <ForecastTab warehouseIds={selectedWarehouseIds} catalogue={catalogue} />}
      {tab === 'anomalies' && <AnomaliesTab warehouseIds={selectedWarehouseIds} />}
      {tab === 'reorder' && <ReorderTab warehouseIds={selectedWarehouseIds} catalogue={catalogue} />}
    </div>
  );
};

export default IntelligencePage;
